import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  Collapse,
  MobileStepper,
  Button,
  Stack,
  Chip,
  Divider,
  Link,
  Paper,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ReactComponent as SelfLearningImage } from '../../assets/images/self-learning.svg';
import { ReactComponent as ContextualUnderstanding } from '../../assets/images/contextual-understanding.svg';
import { ReactComponent as PredictiveAdaptation } from '../../assets/images/predictive-adaptation.svg';
import { ReactComponent as Ethical } from '../../assets/images/ethical.svg';
import { ReactComponent as Brain } from '../../assets/images/brain.svg';
import { ReactComponent as AllLifeStage } from '../../assets/images/all-life-stage.svg';
import ChatBar from '../../assets/images/chat-bar.svg';
import Recent from '../../assets/images/recent-light.svg';
import AddMember from '../../assets/images/add-member.svg';
import { YellowBoxBottom } from './Home';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { StyledImageContainerWithMarginBottom } from '../../components/StyledComponents';
import MacbookFrame from '../../components/MacbookFrame';
import MarriagePredictionsCard from '../../components/MarriagePredictionCard';

const KaliyugAIIntroSection = () => {
  const navigate = useNavigate();
  const { isLogin } = useAuth();
  return (
    <Box>
      {/* Title */}
      <Typography variant="h1" textAlign="center">
        Introducing{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> <br />{' '}
        Based On Vedic Astrology
      </Typography>

      {/* CTA Button */}
      <Box display="flex" justifyContent="center" my={2}>
        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={() => {
            navigate('/chat');
          }}
        >
          Try &nbsp;{' '}
          <span style={{ fontFamily: 'SpaceGrotesk' }}> KaliyugAi</span> &nbsp;
          v1.0s
        </Button>
      </Box>

      <MarriagePredictionsCard />

      <Typography variant="h2" sx={{ mt: 6 }}>
        Ask, Discover and Understand with KaliyugAI
      </Typography>
      <Typography variant="homePageBody">
        A seamless question like “When will I get married?” unfolds into a
        personalized AI-guided journey.
      </Typography>

      <MacbookFrame />

      {/* Section Intro */}
      <Box mt={7}>
        <Typography variant="h3" fontWeight={500}>
          A New Era in Astrological Guidance
        </Typography>

        <Typography variant="homePageBody" paragraph>
          Welcome to a transformative leap in astrology where ancient wisdom
          meets cutting-edge artificial intelligence. Our{' '}
          <strong>Astrology AI Chat System</strong> is not just another chatbot
          pretending to be an astrologer or providing generic answers. It’s a{' '}
          <strong>
            purpose-built, research-backed, and results-driven tool
          </strong>{' '}
          designed to deliver real-time astrological guidance with{' '}
          <strong>over 80% accuracy</strong>.<br />
          Unlike the generic AI systems that claim to be "astrological
          assistants," our system is trained on thousands of authentic
          astrological charts and deep scholarly inputs from leading industry
          experts.
        </Typography>

        <Typography variant="homePageBody" paragraph>
          Our foundation isn’t based on random automated responses. We’ve
          collaborated with top-tier astrologers and researchers, analysing over
          thousands real-life astrological charts, ensuring the system
          understands and implements the 8-level rule structure of Vedic
          astrology a gold standard rarely even known, let alone practiced, by
          many self-proclaimed astrologers.
        </Typography>

        <Typography variant="homePageBody" paragraph>
          Where other systems give vague advice or auto-generated answers, we
          deliver actual predictions with dates and remedies. Every user
          receives the same high-accuracy answer for the same question no mood
          swings, no human error, no fear-mongering. Our AI is{' '}
          <strong>built beyond bias</strong>, ensuring consistency, fairness,
          and professionalism.
        </Typography>
      </Box>

      {/* Divider */}
      <Divider />

      {/* KaliyugAI What's New Section */}
      {/* <Stack direction="row" alignItems="center" spacing={1} mb={1}> */}
      <Typography
        variant="h2"
        fontWeight={600}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          gap: '10px',
        }}
      >
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '-12px',
            gap: '10px',
          }}
        >
          <Typography variant="subtitle2" color="orange" fontWeight={600}>
            version 1.0s
          </Typography>
          <Chip label="Beta" size="small" sx={{ fontSize: '0.7rem' }} />
        </Box>
      </Typography>
      {/* </Stack> */}

      <Typography variant="homePageBody" paragraph>
        What's New
      </Typography>

      <Typography variant="homePageBody" paragraph>
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> v1.0s Beta
        Developer ecosystem Introduces a focused testing phase of our newly
        developed Marriage Segment. In this beta release, we have selectively
        unfolded only the marriage-related features to ensure detailed analysis
        and accurate insights. This segment is designed entirely in-house and
        aims to provide answers to some of the most important questions
        individuals have about their future, such as "When will I get married?"
        and "Will it be a love marriage or an arranged marriage?"
      </Typography>

      <Typography variant="homePageBody" paragraph>
        This is just the beginning of a much broader experience. In upcoming
        versions, we will be expanding to include additional life segments such
        as career, finances, health, and family. We encourage users to stay
        connected and explore each new phase as we continue to evolve{' '}
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> into a
        more insightful and holistic astrological assistant.
      </Typography>
    </Box>
  );
};

const featureData = [
  {
    title: 'Advance self learning',
    image: SelfLearningImage,
    fullDesc: `What sets <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> apart is its modern self-learning engine that evolves with every interaction. Unlike traditional astrology systems bound by fixed rules, <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> continuously analyzes historical chart data and user feedback to refine its predictions. By learning from real-world patterns and outcomes, it adapts over time, offering increasingly accurate and personalized insights. This dynamic approach moves beyond static interpretations, positioning <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> as a superior, intelligent system in the field of astrology one that blends ancient wisdom with the power of modern data-driven intelligence.`,
  },
  {
    title: 'Deep Contextual Understanding',
    image: ContextualUnderstanding,
    fullDesc: `<span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> Beta now integrates advanced contextual processing, allowing it to understand not just the "what," but the "why" behind astrological patterns. By leveraging multi-layered data analysis combining birth charts, life events, and behavior patterns it generates deeply personalized readings. This new approach moves beyond surface-level predictions, offering a truly intuitive experience that mirrors the complexity and depth of real human journeys.`,
  },
  {
    title: 'Real-Time Predictive Adaptation',
    image: PredictiveAdaptation,
    fullDesc: `What sets our Beta version apart is its ability to adapt predictions in real-time. Traditional astrology models rely heavily on static data; however, our system dynamically adjusts its forecasts based on incoming live feedback and updated planetary movements. This ensures that users receive the most accurate, up-to-the-minute insights possible, making <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> a living, breathing intelligence engine rather than a fixed database of interpretations.`,
  },
];

const FeatureCarousel = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  const handleCardClick = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const handleScroll = () => {
    const scrollLeft = containerRef.current.scrollLeft;
    const containerWidth = containerRef.current.offsetWidth;
    const newIndex = Math.round(scrollLeft / containerWidth);
    if (newIndex !== activeStep) setActiveStep(newIndex);
  };

  const scrollToCard = (index) => {
    const el = cardRefs.current[index];
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      });
      setActiveStep(index);
      setExpandedIndex(null);
    }
  };

  return (
    // MT : 80 = 36 + 54
    <Box sx={{ width: '100%', marginTop: '54px' }}>
      {/* Scrollable Cards */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          gap: 4,
          px: 2,
          pb: 2,
          scrollBehavior: 'smooth',
          // Hide scrollbar for Chrome, Safari and Opera
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          // Hide scrollbar for IE, Edge and Firefox
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {featureData.map((feature, index) => (
          <Box
            key={index}
            ref={(el) => (cardRefs.current[index] = el)}
            sx={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              cursor: 'pointer',
            }}
            onClick={() => handleCardClick(index)}
          >
            <Card sx={{ borderRadius: 2, boxShadow: 3, ml: 2 }}>
              <Stack direction="row" spacing={2} p={2} alignItems="flex-start">
                <Box
                  component={featureData[activeStep].image}
                  alt={featureData[activeStep].title}
                />
                {/* Right Column: Title + Description(s) */}
                <Box
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '280px',
                      md: '300px',
                      lg: '335px',
                    },
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.longDesc}
                  </Typography>
                  {expandedIndex === index ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                      dangerouslySetInnerHTML={{ __html: feature.fullDesc }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3, // Allow up to 3 lines
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4,
                        maxHeight: '4.2em', // 3 lines * 1.4 line height
                      }}
                      dangerouslySetInnerHTML={{ __html: feature.fullDesc }}
                    />
                  )}
                </Box>

                {/* Expand/Collapse Icon */}
                <Box display="flex" alignItems="center">
                  {expandedIndex === index ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  )}
                </Box>
              </Stack>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Custom Clickable Dots */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 1,
          gap: 1.5,
        }}
      >
        {featureData.map((_, index) => {
          const isActive = activeStep === index;
          return (
            <Box
              key={index}
              onClick={() => scrollToCard(index)}
              sx={{
                width: isActive ? 24 : 10,
                height: 10,
                borderRadius: 5,
                backgroundColor: isActive ? '#444' : '#fff',
                border: '1px solid #666',
                boxShadow: isActive ? 'inset 0 0 2px #333' : 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const VisionComponent = () => {
  return (
    <Box>
      {/* Header */}
      <Typography
        variant="h2"
        fontWeight={600}
        sx={{ marginBottom: '18px !important' }}
      >
        A Vision Rooted in Purpose, Growing Through Technology
      </Typography>
      <Typography
        variant="subtitle1"
        fontStyle="italic"
        color="text.secondary"
        sx={{ marginBottom: '80px' }}
      >
        In the next 5, we are building an ecosystem that redefines astrology for
        the modern world:
      </Typography>

      {/* Section 1 */}
      {/* Icon */}
      <Box component={Ethical} alt="Ethical Icon" sx={{ width: 80 }} />
      {/* Content */}
      <Box>
        <Typography variant="h3" fontWeight={600}>
          Build the World’s Most Ethical Astrological Intelligence System
        </Typography>
        <Typography variant="homePageBody" paragraph>
          We envision a world where astrology becomes a{' '}
          <strong>trusted life companion</strong>. Where people turn to
          astrological insights with the same confidence they place in modern
          medicine or education.
          <br />
          Where predictive intelligence is not vague or generalized but{' '}
          <strong>personalized, verified, and deeply relevant</strong> to each
          individual's journey, Where ancient scripts like the Brihat Parashara
          Hora Shastra, Phaladeepika, Bhrigu Samhita, and Laghu Parashari are
          honored, studied, and revived through ethical, scientific application.
          <br />
          Our dream is to <strong>revive the lost credibility</strong> of
          astrological sciences globally integrating its profound wisdom into
          everyday decision-making for career, relationships, health, finance,
          and spiritual evolution.
        </Typography>
      </Box>

      {/* Section 2 */}
      <Box
        component={Brain}
        alt="AI Icon"
        sx={{
          width: 80,
          marginTop: 'calc(80px - 36px)',
        }}
      />
      <Box>
        <Typography variant="h3" fontWeight={600}>
          AI That Knows Your Chart Better Than You
        </Typography>
        <Typography variant="homePageBody" paragraph>
          Our engine is constantly learning from user behavior, queries, and
          historical patterns helping us offer hyper-personalized insights.
          Whether it's career choices, marriage timing, or emotional phases, we
          want our AI to act like your cosmic guide accurate, safe, and
          emotionally intuitive.
        </Typography>
      </Box>

      {/* Section 3 */}
      <Box
        component={AllLifeStage}
        alt="Life Stage Icon"
        sx={{
          width: 80,
          marginTop: 'calc(80px - 36px)',
        }}
      />
      <Box>
        <Typography variant="h3" fontWeight={600}>
          One Platform for All Life Stages
        </Typography>
        <Typography variant="homePageBody" paragraph>
          From childbirth to marriage, finance to retirement we’re building a
          single digital space where your life’s decisions can be enriched by
          Vedic intelligence. No need to jump between apps, websites, or
          astrologers. Just open{' '}
          <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAI</span>, ask
          your question, and find aligned answers.
        </Typography>
      </Box>
    </Box>
  );
};

const HomePage = () => {
  return (
    <Box>
      <KaliyugAIIntroSection />
      <FeatureCarousel />
      <YellowBoxBottom sx={{ textAlign: 'left' }}>
        <Typography variant="h3">
          Precision Meets Purpose | Not Just Another App
        </Typography>
        <Typography variant="homePageBody">
          This is not an Astrology Gimmick chatbot that throws out random
          predictions or uses fear-based sales tactics. Our system is
          purpose-built to replace the wave of fake astrologers flooding the
          market many of whom lack deep knowledge of this science. Even several
          top apps today operate without a solid foundation in core astrological
          principles. They offer inconsistent advice, contradict themselves, and
          barely reach 30% accuracy, leaving users confused, misled, and without
          meaningful direction.
          <br /> We’ve taken a different path.
          <br />
          Our AI isn’t built on generic shortcuts from off-the-shelf models.
          Instead, we’ve developed a standalone intelligence layer trained
          relentlessly on real-world data and refined using timeless texts like
          the Brihat Parashara Hora Shastra, Phaladeepika, Bhrigu Samhita, and
          Laghu Parashari. Every insight is grounded in authentic astrological
          knowledge, enriched by practical, ethically guided solutions.
          <br />
          <b>
            We don’t sell fear. <br />
            We offer clarity.
          </b>
          <br />
          This is astrology, redefined with precision and purpose.
        </Typography>
      </YellowBoxBottom>
      <Divider />
      <Box mt={4}>
        <Typography variant="h2" fontWeight={600}>
          Explore Features of Chat System
        </Typography>
        <Typography variant="homePageBody" paragraph>
          Chat system is a dynamic, intelligent gateway instantly navigating
          users to sections like "Birth Time Settings" or Open any chat from
          "Recent History" just by typing a command. It continuously learns from
          platform wide user behavior: the more a question is asked, the more
          prominently it appears in the chat, helping users quickly find
          popular, high demand answers.This transforms the chatbox into a
          real-time, self-optimizing assistant making navigation and support
          smarter, faster, and more intuitive.
        </Typography>
        <StyledImageContainerWithMarginBottom>
          <img src={ChatBar} alt="chat system" />
        </StyledImageContainerWithMarginBottom>
        <Typography variant="homePageBody" paragraph>
          Our system smartly organizes your questions for easy access.The top 3
          recent questions you ask are always shown under "Recent".Other queries
          automatically sort into categories like Marriage, Finance, etc.,
          making it effortless for you to revisit your past chats and continue
          where you left off all without any confusion.
        </Typography>
        <StyledImageContainerWithMarginBottom>
          <img src={Recent} alt="recent chats" />
        </StyledImageContainerWithMarginBottom>
        <Typography variant="homePageBody" paragraph>
          One of <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAI’s</span>{' '}
          unique strengths lies in its AI-powered Birth Time Rectification
          (BTR). In many cases, users are unsure of their exact birth time or
          only have an approximate range. Our intelligent system steps in to
          automatically tweak and refine the given time when it detects that the
          input is close but not entirely accurate.
          <br />
          Using advanced astrological logic and predictive validation models,
          our AI analyzes planetary alignments, past life events, and your
          responses to fine-tune the birth time internally without disrupting
          the user experience.
        </Typography>
        <Typography variant="homePageBody" paragraph>
          This process:
          <br />
          Is entirely automatic no extra steps required from the user. Only
          occurs when the system finds the input time is reasonably near the
          actual time. Enhances the accuracy of your predictions, especially in
          sensitive areas like marriage, career, or health.
        </Typography>
        <Typography variant="homePageBody" paragraph>
          And here’s the best part we currently offer this BTR service
          absolutely free. You’ll also be notified that a BTR adjustment was
          made, and can even view the rectified birth time our system used for
          predictions. By quietly correcting small errors in birth time, we help
          ensure that your insights are more aligned, timely, and trustworthy
          even if you weren’t sure about the exact minute you were born.
        </Typography>
        <Typography variant="homePageBody" paragraph>
          We’re continually improving this feature to make it even more
          powerful, reliable, and adaptive ensuring that as our AI grows
          smarter, your astrological guidance becomes even more personalized,
          accurate, and future-ready.
        </Typography>
        {/* Notice box */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Paper
            sx={{
              border: '1px solid #e0e0e0',
              padding: 2,
              boxShadow: 'none',
              marginBottom: '100px',
            }}
          >
            <Typography variant="body2" color="warning.main">
              Your birth time has been auto-adjusted by{' '}
              <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> for
              enhanced accuracy (BTR applied).
            </Typography>
          </Paper>
        </Box>
        <Typography variant="subtitle1" fontWeight={600}>
          Add Member Feature Simplifying Your Astrological Experience
        </Typography>

        <Typography variant="homePageBody" paragraph>
          With the Add Member feature, you can effortlessly create and manage
          horoscopes for your loved ones without the hassle of creating new
          accounts or constantly editing personal details. <br />
          Simply add a member, fill in their basic information, and instantly
          view their complete chart and predictions directly within the chat
          screen.
        </Typography>

        <StyledImageContainerWithMarginBottom
          sx={{ marginBottom: '0px !important' }}
        >
          <img src={AddMember} alt="add members" />
        </StyledImageContainerWithMarginBottom>

        <Divider />

        <VisionComponent />

        <YellowBoxBottom>
          <Typography variant="h2">
            Astrology made simple, personal <br /> and powerful. Experience
            KaliyugAi.
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            Your thoughts matter to us! Reach out anytime at
            <br />
            <Link href="mailto:support@kaliyugai.com" color="secondary">
              support@kaliyugai.com
            </Link>
          </Typography>
        </YellowBoxBottom>
      </Box>
    </Box>
  );
};

export default HomePage;
